import {
  DereferencedTargetingDataType,
  TargetingDataType,
  VisitorReference,
} from 'src/targeting';
import { DataInfoType, FeatureFlagVariationsType, IStorage } from 'src/storage';
import {
  Browser,
  Conversion,
  Cookie,
  CustomData,
  Device,
  GeolocationData,
  OperatingSystem,
  PageView,
  UserAgent,
  ApplicationVersion,
  VisitsData,
  KameleoonConversionScore,
  DataManager,
  UniqueIdentifier,
} from 'src/kameleoonData';
import { constructTypeMap } from './helpers';
import { VisitorDataFiltersType } from 'src/utilities';
import { TrackingStatus } from 'src/types';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import { Personalization } from 'src/kameleoonData/personalization';
import { CBScores } from 'src/kameleoonData/cbScores';
import { TargetedSegment } from 'src/kameleoonData/targetedSegment';

export type FieldParametersType = {
  expirationTime: number;
  key: KameleoonData;
  value:
    | BrowserDataType
    | CookieDataType
    | DeviceDataType
    | GeolocationDataType
    | OperatingSystemDataType
    | UserAgentDataType
    | VisitsDataType;
  visitorCode: string;
  data: DereferencedTargetingDataType;
};

export type VisitorProcessorParametersType = {
  filters: VisitorDataFiltersType;
  dataManager: DataManager;
  visitorCode: string;
};

export type NestedFieldParametersType = {
  expirationTime: number;
  visitorCode: string;
  key:
    | KameleoonData.CustomData
    | KameleoonData.PageView
    | KameleoonData.Conversion
    | KameleoonData.Personalization
    | KameleoonData.TargetedSegment;
  nestedKey: string;
  value:
    | CustomDataType
    | PageViewDataType
    | ConversionDataType
    | PersonalizationDataType
    | TargetedSegmentDataType;
  data: DereferencedTargetingDataType;
};

export type DataFilterType = ({
  status,
  key,
  cdIndex,
}: {
  status: TrackingStatus;
  key: KameleoonData;
  cdIndex?: number;
}) => boolean;

export type MutAddUnsentDataType = {
  mutData: DataInfoType;
  visitorCode: string;
  dataType: KameleoonData;
};

export type DereferenceTargetingDataResultType = {
  data: DereferencedTargetingDataType;
  visitorReference: string;
  isReference: boolean;
};

export type DataManagerParametersType = {
  cleanupInterval: number | null;
  dataStorage: IStorage<TargetingDataType>;
  infoStorage: IStorage<DataInfoType>;
  packageInfo: ExternalPackageInfoType;
};

export type KameleoonDataItemType =
  | IdentifierDataType
  | BrowserDataType
  | ConversionDataType
  | CustomDataType
  | DeviceDataType
  | PageViewDataType
  | UserAgentDataType
  | CookieDataType
  | GeolocationDataType
  | OperatingSystemDataType
  | ApplicationVersionDataType
  | KCSDataType
  | VisitsDataType
  | PersonalizationDataType
  | CBSDataType
  | TargetedSegmentDataType;

/**
 * @interface an interface of KameleoonData
 * each class instance will have url getter method
 * for handling url of different data types
 * */
export interface IKameleoonData {
  /**
   * @property url - url for the current data type used for events tracking
   * */
  readonly url: string;
  /**
   * @property data - data of the current class instance
   * */
  readonly data: KameleoonDataItemType;
  /**
   * @property status - signifying if the data is tracked, not tracked or pending
   * */
  status: TrackingStatus;
}

/**
 * @type a tuple of each possible type of data for `addData` method
 * of KameleoonClient
 * */
export type KameleoonDataType =
  | UniqueIdentifier
  | Browser
  | Conversion
  | CustomData
  | Device
  | PageView
  | UserAgent
  | VisitsData
  | GeolocationData
  | OperatingSystem
  | Cookie
  | ApplicationVersion
  | KameleoonConversionScore
  | Personalization
  | CBScores
  | TargetedSegment;

export type ExpirationTimeType = {
  expirationTime: number;
};

export type VisitorsDataType = {
  [visitorCode: string]: KameleoonDataType[];
};

export type ExistingCustomDataType = NonNullable<
  KameleoonVisitorDataType[KameleoonData.CustomData]
>;

export type ExistingPageViewDataType = NonNullable<
  KameleoonVisitorDataType[KameleoonData.PageView]
>;

export type ExistingConversionDataType = NonNullable<
  KameleoonVisitorDataType[KameleoonData.Conversion]
>;

export type ExistingPersonalizationDataType = NonNullable<
  KameleoonVisitorDataType[KameleoonData.Personalization]
>;

export type ExistingTargetedSegmentDataType = NonNullable<
  KameleoonVisitorDataType[KameleoonData.TargetedSegment]
>;

export type ExistingDataType<T extends KameleoonData> = NonNullable<
  KameleoonVisitorDataType[T]
>;

export type ExistingNestedDataType =
  | ExistingCustomDataType
  | ExistingPageViewDataType
  | ExistingConversionDataType
  | ExistingPersonalizationDataType
  | ExistingTargetedSegmentDataType;

export type KameleoonVisitorDataType<T = ExpirationTimeType> = {
  [KameleoonData.CustomData]?: {
    [index: string]: (CustomDataType & T) | undefined;
  };
  [KameleoonData.Conversion]?: {
    [id: string]: (ConversionDataType & T) | undefined;
  };
  [KameleoonData.PageView]?: { [url: string]: PageViewDataType & T };
  [KameleoonData.UniqueIdentifier]?: IdentifierDataType & T;
  [KameleoonData.GeolocationData]?: GeolocationDataType & T;
  [KameleoonData.UserAgent]?: UserAgentDataType & T;
  [KameleoonData.Browser]?: BrowserDataType & T;
  [KameleoonData.Device]?: DeviceDataType & T;
  [KameleoonData.Cookie]?: CookieDataType & T;
  [KameleoonData.KameleoonConversionScore]?: KCSDataType & T;
  [KameleoonData.OperatingSystem]?: OperatingSystemDataType & T;
  [KameleoonData.ApplicationVersion]?: ApplicationVersionDataType & T;
  [KameleoonData.VisitsData]?: VisitsDataType & T;
  [KameleoonData.Personalization]?: {
    [id: string]: PersonalizationDataType & T;
  };
  [KameleoonData.CBScores]?: CBSDataType & T;
  [KameleoonData.TargetedSegment]?: {
    [id: string]: TargetedSegmentDataType & T;
  };
};

export enum KameleoonData {
  UniqueIdentifier = 'uniqueIdentifier',
  CustomData = 'customData',
  PageView = 'pageView',
  GeolocationData = 'geolocation',
  Browser = 'browser',
  Conversion = 'conversion',
  Cookie = 'cookie',
  Device = 'device',
  OperatingSystem = 'operatingSystem',
  UserAgent = 'userAgent',
  ApplicationVersion = 'applicationVersion',
  VisitsData = 'visitsData',
  KameleoonConversionScore = 'kameleoonConversionScore',
  Personalization = 'personalization',
  CBScores = 'cbScores',
  TargetedSegment = 'targetedSegment',
}

type CommonUpdateParametersType = {
  mutData: DereferencedTargetingDataType;
  visitorCode: string;
  expirationTime: number;
};

export type UpdateCustomDataParametersType = {
  customData: CustomData;
} & CommonUpdateParametersType;

export type ProcessCustomDataManagerParametersType = {
  infoData: DataInfoType;
  mutData: TargetingDataType;
  customData: CustomData;
  visitorCode: string;
};

export type UpdateConversionParametersType = {
  conversion: Conversion;
} & CommonUpdateParametersType;

export type UpdateVisitsDataParametersType = {
  visitsData: VisitsData;
} & CommonUpdateParametersType;

export type UpdatePersonalizationParametersType = {
  personalization: Personalization;
} & CommonUpdateParametersType;

export type UpdateTargetSegmentParametersType = {
  targetedSegment: TargetedSegment;
} & CommonUpdateParametersType;

export type UpdateCommonDataParametersType = {
  commonData: KameleoonDataType;
} & CommonUpdateParametersType;

export type UpdatePageViewParametersType = {
  pageView: PageView;
} & CommonUpdateParametersType;

export type MutUpdateDataParametersType = {
  infoData: DataInfoType;
  mutData: TargetingDataType;
  dataItem: KameleoonDataType;
  visitorCode: string;
};

export type MutUpdateTargetingDataParametersType = {
  visitorCode: string;
  infoData: DataInfoType;
  kameleoonData: KameleoonDataType[];
  targetingData: TargetingDataType;
};

export type ReferenceParametersType = {
  mutData: TargetingDataType;
  visitorCode: string;
  linkedVisitor: VisitorReference;
  infoData: DataInfoType;
};

type SharedDataPropertiesType = {
  type: KameleoonData;
  status: TrackingStatus;
};

export type IdentifierDataType = {
  value: boolean;
} & SharedDataPropertiesType;

export type BrowserDataType = {
  browser: BrowserType;
  version?: number;
} & SharedDataPropertiesType;

export type TargetedSegmentDataType = {
  id: number;
} & SharedDataPropertiesType;

export type ConversionDataType = {
  id: number;
  goalId: number;
  revenue: number;
  negative: boolean;
  nonce?: string;
  metadata?: CustomDataType[];
} & SharedDataPropertiesType;

export type CustomDataType = {
  index: number;
  value: string[];
  isIdentifier: boolean;
} & SharedDataPropertiesType;

export type DeviceDataType = {
  device: DeviceType;
} & SharedDataPropertiesType;

export type KCSDataType = {
  kcs: KCSType;
} & SharedDataPropertiesType;

export type CBSDataType = {
  cbs: CBSType;
} & SharedDataPropertiesType;

export type PersonalizationDataType = {
  id: number;
  variationId: number;
} & SharedDataPropertiesType;

export type VisitsDataType = {
  visits: VisitDataType[] | number[];
  visitNumber?: number;
} & SharedDataPropertiesType;

export type VisitDataType = {
  timeStarted: number;
  timeLastActivity: number;
};

export type OperatingSystemDataType = {
  operatingSystem: OperatingSystemType;
} & SharedDataPropertiesType;

export type CookieDataType = {
  cookie: CookieType[];
} & SharedDataPropertiesType;

export type GeolocationDataType = GeolocationInfoType &
  SharedDataPropertiesType;

export type PageViewDataType = {
  urlAddress: string;
  title: string;
  nonce?: string;
  referrers?: number[];
  timestamps: TimestampType[];
} & SharedDataPropertiesType;

export type TimestampType = {
  time: number;
  status: TrackingStatus;
};

export type UserAgentDataType = {
  value: string;
} & SharedDataPropertiesType;

export type ApplicationVersionDataType = {
  version: string;
} & SharedDataPropertiesType;

/**
 * @param {number} goalId - an id of a goal to track
 * @param {number | undefined} revenue - an optional parameter for revenue, default value is `0`
 * @param {boolean | undefined} negative - an optional parameter identifying whether the conversion should be removed, default value is `false`
 * @param {CustomData[] | undefined} metadata - an optional parameter, default value is `undefined`
 * */
export type ConversionParametersType = {
  goalId: number;
  revenue?: number;
  negative?: boolean;
  metadata?: CustomData[];
};

/**
 * @param {string} urlAddress - url address of the page to track
 * @param {string} title - title of the web page
 * @param {number[] | undefined} referrers - an optional parameter containing a list of referrersIndices, has no default value
 * */
export type PageViewParametersType = {
  urlAddress: string;
  title: string;
  referrers?: number[];
};

/**
 * @readonly
 * @enum {string} an Enum containing all possible variants for device type
 * */
export enum DeviceType {
  Phone = 'PHONE',
  Tablet = 'TABLET',
  Desktop = 'DESKTOP',
}

/**
 * @readonly
 * @enum {string} an Enum containing all possible variants for operating system type
 * */
export enum OperatingSystemType {
  WindowsPhone = 'WINDOWS_PHONE',
  Windows = 'WINDOWS',
  Android = 'ANDROID',
  Linux = 'LINUX',
  Mac = 'MAC',
  IOS = 'IOS',
}

/**
 * @param {string} country - a country of a visitor
 * @param {string | undefined} region - an optional parameter containing a region of a visitor, has no default value
 * @param {string | undefined} city - an optional parameter containing a city of a visitor, has no default value
 * @param {string | undefined} postalCode - an optional parameter containing a postal code of a visitor, has no default value
 * @param {[number, number] | undefined} coordinates - an optional parameter containing a pair of coordinates (longitude and latitude) of a visitor, has no default value. Coordinate `number` represents decimal degrees (double that can be negative)
 * */
export type GeolocationInfoType = {
  country: string;
  region?: string;
  city?: string;
  postalCode?: string;
  coordinates?: [number, number];
};

/**
 * @param {string} key - a key of a cookie
 * @param {string} value - a value of a cookie
 * */
export type CookieType = {
  key: string;
  value: string;
};

/**
 * @type Nested object of key moments and goals with their respective scores
 * */
export type KCSType = {
  [keyMoment: string]: {
    [goalId: string]: number;
  };
};

export type CBSType = {
  [key: string]: ScoredVarIds;
};

export type ScoredVarIds = {
  [variationId: string]: number;
};

export type VarGroup = {
  ids: number[];
};

export type OptionalCookieType = Omit<CookieType, 'key'> & {
  key?: string;
};

/**
 * @readonly
 * @enum {string} an Enum containing all possible variants for browser type
 * */
export enum BrowserType {
  Chrome = 'CHROME',
  InternetExplorer = 'IE',
  Firefox = 'FIREFOX',
  Safari = 'SAFARI',
  Opera = 'OPERA',
  Other = 'OTHER',
}

export const OperatingSystemIndexMap = {
  [OperatingSystemType.Windows]: 0,
  [OperatingSystemType.Mac]: 1,
  [OperatingSystemType.IOS]: 2,
  [OperatingSystemType.Linux]: 3,
  [OperatingSystemType.Android]: 4,
  [OperatingSystemType.WindowsPhone]: 5,
} as const;

export const OperatingSystemTypeMap = constructTypeMap<OperatingSystemType>(
  OperatingSystemIndexMap,
);

export const BrowserIndexMap = {
  [BrowserType.Chrome]: 0,
  [BrowserType.InternetExplorer]: 1,
  [BrowserType.Firefox]: 2,
  [BrowserType.Safari]: 3,
  [BrowserType.Opera]: 4,
  [BrowserType.Other]: 5,
} as const;

export const BrowserTypeMap = constructTypeMap<BrowserType>(BrowserIndexMap);

export type SingleDataType = {
  [KameleoonData.Device]: Device | null;
  [KameleoonData.Browser]: Browser | null;
  [KameleoonData.GeolocationData]: GeolocationData | null;
  [KameleoonData.OperatingSystem]: OperatingSystem | null;
  [KameleoonData.KameleoonConversionScore]: KameleoonConversionScore | null;
  [KameleoonData.CBScores]: CBScores | null;
};

export type MultipleDataType = {
  [KameleoonData.CustomData]: Map<number, CustomData>;
  [KameleoonData.PageView]: Map<string, PageView>;
  [KameleoonData.Conversion]: Map<number, Conversion>;
  [KameleoonData.Personalization]: Map<number, Personalization>;
};

export type VisitProcessorDataType = {
  visitorData: KameleoonDataType[];
  storageVisitorData: KameleoonDataType[];
  visitsData: VisitsData;
  experimentData: FeatureFlagVariationsType;
};

export type StaticDataType = {
  browserType?: BrowserType;
  browserVersion?: number;
  visitNumber?: number;
  timeSincePreviousVisit?: number;
  deviceType?: DeviceType;
  os?: OperatingSystemType;
};
